import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { BotonCambiarHogar } from '@/components/CambiarHogar'
import { incomes } from '@/lib/api'
import { useSession } from '@/lib/session'
import { Onboarding } from '@/routes/Onboarding'

/**
 * Decide si mostrar el asistente de configuración.
 *
 * El criterio es tener al menos una fuente de ingreso: sin ella no hay periodo
 * de gasto y el disponible —la cifra por la que existe la app— no se puede
 * calcular. Es la única pieza sin la cual el tablero mentiría.
 *
 * Basta **una**: quien cobra sueldo y además factura añade la segunda desde
 * Ajustes. Exigir todas de entrada convertiría el alta en un formulario largo
 * justo donde hay que ganarse a alguien que aún no sabe si la app le sirve.
 */
export function OnboardingGate({ children }: { children: ReactNode }) {
  const { membership, refresh } = useSession()

  // El laboratorio de componentes no configura nada del hogar, así que pasarlo
  // por el asistente solo impediría verlo. Solo existe en desarrollo.
  const enLaboratorio = import.meta.env.DEV && window.location.pathname === '/lab'

  const { data, isPending, refetch } = useQuery({
    queryKey: ['incomes', membership.id],
    queryFn: () => incomes.list(membership.id),
    retry: 1,
    enabled: !enLaboratorio,
  })

  if (enLaboratorio) return <>{children}</>

  // Mientras se resuelve no se decide nada: mostrar el asistente por un instante
  // a quien ya lo completó sería peor que esperar.
  if (isPending) return null

  if (!data || data.length === 0) {
    return (
      <>
        <Onboarding
          memberId={membership.id}
          householdKind={membership.household.kind}
          onDone={() => {
            void refetch()
            void refresh()
          }}
        />
        {/* La salida, para quien pertenece a más de un hogar.
            Aceptar una invitación deja activo el hogar nuevo, y uno recién
            estrenado no tiene ingresos, así que el asistente se pone delante de
            todo. Sin este botón, quien ya tenía su propio hogar entra al de su
            pareja y no puede volver al suyo: Ajustes queda detrás del
            asistente y la única salida sería cerrar sesión. */}
        <BotonCambiarHogar />
      </>
    )
  }

  return <>{children}</>
}
